// Yahoo Finance Service - Real-time data from Yahoo Finance API
// Using Yahoo Finance web API endpoints for real-time market data

export interface YahooFinanceQuote {
  symbol: string;
  regularMarketPrice: number;
  regularMarketChange: number;
  regularMarketChangePercent: number;
  regularMarketVolume?: number;
  marketState: string;
  regularMarketTime: number;
}

export interface YahooFinanceResponse {
  quoteResponse: {
    result: YahooFinanceQuote[];
    error: null | string;
  };
}

// Yahoo Finance symbol mappings - Using REAL-TIME accurate Yahoo Finance tickers
// Updated with correct symbols for real-time data matching Yahoo Finance website
export const YAHOO_SYMBOL_MAPPINGS: Record<string, { ticker: string; name: string }> = {
  // Currency pairs - Real-time forex rates
  'DXY': { ticker: 'DX=F', name: 'DXY (USD)' }, // US Dollar Index Futures
  'EUR': { ticker: 'EURUSD=X', name: 'EUR/USD' },
  'GBP': { ticker: 'GBPUSD=X', name: 'GBP/USD' },
  'CHF': { ticker: 'USDCHF=X', name: 'USD/CHF' },
  'AUD': { ticker: 'AUDUSD=X', name: 'AUD/USD' },
  'NZD': { ticker: 'NZDUSD=X', name: 'NZD/USD' },
  'CAD': { ticker: 'USDCAD=X', name: 'USD/CAD' },
  'JPY': { ticker: 'USDJPY=X', name: 'USD/JPY' },
  
  // Stock Index Futures - Real-time futures contracts
  'SP500': { ticker: 'ES=F', name: 'S&P 500 Futures' },
  'NASDAQ': { ticker: 'NQ=F', name: 'NASDAQ 100 Futures' },
  'DOW': { ticker: 'YM=F', name: 'Dow Jones Futures' },
  
  // Commodities Futures - Real-time commodity prices
  'GOLD': { ticker: 'GC=F', name: 'Gold Futures' }, // Gold Dec 24
  'SILVER': { ticker: 'SI=F', name: 'Silver Futures' }, // Silver Dec 24
  'COPPER': { ticker: 'HG=F', name: 'Copper Futures' }, // Copper Dec 24
  'PLATINUM': { ticker: 'PL=F', name: 'Platinum Futures' }, // Platinum Jan 25
  
  // Energy Futures - Real-time energy prices
  'CRUDE': { ticker: 'CL=F', name: 'Crude Oil Futures' }, // WTI Crude Oil
  'GASOLINE': { ticker: 'RB=F', name: 'Gasoline Futures' }, // RBOB Gasoline
  'NATGAS': { ticker: 'NG=F', name: 'Natural Gas Futures' } // Natural Gas
};

class YahooFinanceService {
  private readonly BASE_URL = 'https://query1.finance.yahoo.com/v7/finance/quote';
  private readonly CORS_PROXY = 'https://api.allorigins.win/raw?url=';
  private cache = new Map<string, { data: any; timestamp: number }>();
  private readonly CACHE_DURATION = 30000; // 30 seconds
  private failureCount = 0;
  private readonly MAX_FAILURES = 3;
  private circuitBreakerOpen = false;
  private lastFailureTime = 0;
  private readonly CIRCUIT_BREAKER_TIMEOUT = 300000; // 5 minutes

  // Check if circuit breaker should be reset
  private checkCircuitBreaker(): void {
    if (this.circuitBreakerOpen && Date.now() - this.lastFailureTime > this.CIRCUIT_BREAKER_TIMEOUT) {
      this.circuitBreakerOpen = false;
      this.failureCount = 0;
      console.log('Yahoo Finance circuit breaker reset');
    }
  }

  // Record API failure
  private recordFailure(): void {
    this.failureCount++;
    this.lastFailureTime = Date.now();
    
    if (this.failureCount >= this.MAX_FAILURES) {
      this.circuitBreakerOpen = true;
      console.warn(`Yahoo Finance circuit breaker opened after ${this.failureCount} failures`);
    }
  }

  // Validate if data is real-time and not demo/delayed
  private validateRealTimeData(quote: YahooFinanceQuote, symbol: string): boolean {
    // Check if data is recent (within last 5 minutes for active markets)
    const now = Date.now();
    const quoteTime = quote.regularMarketTime * 1000;
    const timeDiff = now - quoteTime;
    
    // For forex markets (24/5), data should be very recent
    if (['EUR', 'GBP', 'CHF', 'AUD', 'NZD', 'CAD', 'JPY'].includes(symbol)) {
      if (timeDiff > 300000) { // 5 minutes
        console.warn(`⚠️ FOREX data for ${symbol} is ${Math.round(timeDiff/60000)} minutes old - may be delayed`);
        return false;
      }
    }
    
    // Check for valid price data
    if (!quote.regularMarketPrice || quote.regularMarketPrice <= 0) {
      console.warn(`⚠️ Invalid price data for ${symbol}: ${quote.regularMarketPrice}`);
      return false;
    }
    
    // Check market state
    if (quote.marketState === 'CLOSED' && timeDiff > 86400000) { // 24 hours
      console.warn(`⚠️ Market closed data for ${symbol} is over 24 hours old`);
      return false;
    }
    
    console.log(`✅ Real-time data validated for ${symbol}: $${quote.regularMarketPrice} (${quote.marketState})`);
    return true;
  }

  // Fetch quote data from Yahoo Finance with real-time validation
  async fetchQuote(symbol: string): Promise<YahooFinanceQuote | null> {
    if (this.circuitBreakerOpen) {
      this.checkCircuitBreaker();
      if (this.circuitBreakerOpen) {
        throw new Error('Yahoo Finance circuit breaker is open');
      }
    }

    const mapping = YAHOO_SYMBOL_MAPPINGS[symbol];
    if (!mapping) {
      throw new Error(`Symbol ${symbol} not found in Yahoo Finance mappings`);
    }

    const cacheKey = `yahoo_${symbol}`;
    const cached = this.cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.data;
    }

    try {
      console.log(`🔄 Fetching REAL-TIME data for ${symbol} (${mapping.ticker})...`);
      
      const url = `${this.BASE_URL}?symbols=${mapping.ticker}`;
      const proxiedUrl = `${this.CORS_PROXY}${encodeURIComponent(url)}`;
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch(proxiedUrl, {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
          'Referer': 'https://finance.yahoo.com/'
        }
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data: YahooFinanceResponse = await response.json();
      
      if (data.quoteResponse.error) {
        throw new Error(data.quoteResponse.error);
      }
      
      if (!data.quoteResponse.result || data.quoteResponse.result.length === 0) {
        throw new Error('No data returned from Yahoo Finance');
      }
      
      const quote = data.quoteResponse.result[0];
      
      // Validate real-time data quality
      const isRealTime = this.validateRealTimeData(quote, symbol);
      if (!isRealTime) {
        console.warn(`⚠️ Data quality warning for ${symbol} - continuing with available data`);
      }
      
      // Log successful data fetch
      console.log(`📊 ${symbol}: $${quote.regularMarketPrice} (${quote.regularMarketChangePercent?.toFixed(2)}%) - ${quote.marketState}`);
      
      // Reset failure count on success
      this.failureCount = 0;
      
      this.cache.set(cacheKey, { data: quote, timestamp: Date.now() });
      return quote;
      
    } catch (error: any) {
      console.error(`❌ Yahoo Finance API error for ${symbol}:`, error.message);
      this.recordFailure();
      
      if (error.name === 'AbortError') {
        throw new Error('Yahoo Finance request timeout');
      }
      
      throw error;
    }
  }

  // Fetch multiple quotes
  async fetchMultipleQuotes(symbols: string[]): Promise<Map<string, YahooFinanceQuote>> {
    const results = new Map<string, YahooFinanceQuote>();
    
    // Process in batches to avoid overwhelming the API
    const batchSize = 5;
    for (let i = 0; i < symbols.length; i += batchSize) {
      const batch = symbols.slice(i, i + batchSize);
      
      const promises = batch.map(async (symbol) => {
        try {
          const quote = await this.fetchQuote(symbol);
          if (quote) {
            results.set(symbol, quote);
          }
        } catch (error) {
          console.warn(`Failed to fetch Yahoo Finance data for ${symbol}:`, error);
        }
      });
      
      await Promise.allSettled(promises);
      
      // Add delay between batches to respect rate limits
      if (i + batchSize < symbols.length) {
        await new Promise(resolve => setTimeout(resolve, 1000)); // 1 second delay
      }
    }
    
    return results;
  }

  // Get symbol mapping
  getSymbolMapping(symbol: string): { ticker: string; name: string } | null {
    return YAHOO_SYMBOL_MAPPINGS[symbol] || null;
  }

  // Get all available symbols
  getAvailableSymbols(): string[] {
    return Object.keys(YAHOO_SYMBOL_MAPPINGS);
  }

  // Check if service is available
  isAvailable(): boolean {
    return !this.circuitBreakerOpen;
  }

  // Reset circuit breaker manually
  resetCircuitBreaker(): void {
    this.circuitBreakerOpen = false;
    this.failureCount = 0;
    this.lastFailureTime = 0;
    console.log('Yahoo Finance circuit breaker manually reset');
  }

  // Get quote for a single symbol (alias for fetchQuote)
  async getQuote(symbol: string): Promise<YahooFinanceQuote> {
    const quote = await this.fetchQuote(symbol);
    if (!quote) {
      throw new Error(`No quote data available for ${symbol}`);
    }
    return quote;
  }
}

export const yahooFinanceService = new YahooFinanceService();
export default yahooFinanceService;